// CloudFront configuration for photo serving with Dynamic Image Transformation
// Using CloudFront distribution to enable:
// - Automatic format conversion (WebP, AVIF for modern browsers)
// - Automatic quality adjustment
// - Viewport-appropriate sizing via URL parameters
// - Edge caching for performance

const CLOUDFRONT_DOMAIN = process.env.REACT_APP_CLOUDFRONT_DOMAIN || 'dd0miujieq36j.cloudfront.net';
const S3_BUCKET = process.env.REACT_APP_S3_BUCKET || 'travel-site-433547283101-us-east-1-an';
const S3_REGION = process.env.REACT_APP_S3_REGION || 'us-east-1';

/**
 * Build a CloudFront URL with Dynamic Image Transformation parameters
 * @param {string} s3Key - S3 object key (e.g., '002new_zealand/photo.jpg')
 * @param {object} options - Transformation options (width, quality, format, etc.)
 * @returns {string} CloudFront URL with DIT parameters
 */
export const getCloudfrontUrl = (s3Key, options = {}) => {
  if (!s3Key) return '';

  const {
    width,
    quality = 80,
    format, // 'webp', 'avif', 'jpeg', 'png' - CloudFront will convert automatically
  } = options;

  // Build query parameters for CloudFront Dynamic Image Transformation
  const params = new URLSearchParams();

  if (width) {
    params.append('width', Math.round(width));
  }

  // Always set quality for consistent optimization
  params.append('quality', quality);

  // Format parameter enables automatic conversion
  // CloudFront will serve WebP to compatible browsers, original format to others
  if (format) {
    params.append('format', format);
  }

  const queryString = params.toString();
  const url = `https://${CLOUDFRONT_DOMAIN}/${s3Key}`;

  return queryString ? `${url}?${queryString}` : url;
};

/**
 * Get responsive image URL for a specific viewport width
 * @param {string} s3Key - S3 object key
 * @param {object} options - Transformation options
 * @returns {string} CloudFront URL with DIT parameters
 */
export const getResponsivePhotoUrl = (s3Key, options = {}) => {
  // Detect viewport width if not provided
  const width = options.width || (typeof window !== 'undefined' ? window.innerWidth : 1200);

  return getCloudfrontUrl(s3Key, {
    width,
    quality: options.quality || 80,
    format: options.format,
  });
};

/**
 * Get thumbnail URL (small, high quality)
 * @param {string} s3Key - S3 object key
 * @returns {string} CloudFront URL optimized for thumbnail
 */
export const getThumbnailUrl = (s3Key) => {
  return getCloudfrontUrl(s3Key, {
    width: 300,
    quality: 85,
  });
};

/**
 * Get hero image URL (large, optimized)
 * @param {string} s3Key - S3 object key
 * @returns {string} CloudFront URL optimized for hero image
 */
export const getHeroImageUrl = (s3Key) => {
  return getCloudfrontUrl(s3Key, {
    width: typeof window !== 'undefined' ? window.innerWidth : 1920,
    quality: 75,
  });
};

/**
 * Get carousel image URL (full-width optimized)
 * @param {string} s3Key - S3 object key
 * @returns {string} CloudFront URL optimized for carousel
 */
export const getCarouselImageUrl = (s3Key) => {
  return getCloudfrontUrl(s3Key, {
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    quality: 80,
  });
};

const CloudFrontConfig = {
  S3_BUCKET,
  S3_REGION,
  getCloudfrontUrl,
  getResponsivePhotoUrl,
  getThumbnailUrl,
  getHeroImageUrl,
  getCarouselImageUrl,
};

export default CloudFrontConfig;
